import {
  Pin, Star, Archive, Trash, Globe, Clock,
  MoreHorizontal, ArrowLeft, Undo2,
  FolderOpen, Tag, FileDown,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { exportNoteToPDF } from '@/lib/pdfExport';

/**
 * Top bar of the Editor: back button, journal badge, pin/fav, and the "more" dropdown.
 */
export default function EditorHeaderBar({
  selectedNote, selectedNoteId, setSelectedNoteId,
  isJournalEntry, activeView, noteNotebook,
  togglePin, toggleFavorite, archiveNote, deleteNote,
  restoreNote, permanentDeleteNote,
  setShowPublish, setShowReminder, setShowHistory,
}) {
  return (
    <div className="flex items-center justify-between px-2 sm:px-4 py-1.5 border-b border-border/50 min-h-[48px]">
      <div className="flex items-center gap-1 sm:gap-2">
        <button onClick={() => setSelectedNoteId(null)} className="md:hidden flex items-center justify-center w-10 h-10 rounded-sm text-muted-foreground hover:text-foreground active:bg-muted/50" data-testid="editor-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        {isJournalEntry && (
          <span className="text-xs text-accent-amber font-medium px-2 py-1 bg-accent-amber/10 rounded-md" data-testid="editor-journal-badge">
            Journal
          </span>
        )}
      </div>
      <div className="flex items-center gap-0.5">
        <button onClick={() => togglePin(selectedNoteId)} className={`p-2 rounded-sm transition-colors ${selectedNote.is_pinned ? 'text-accent-amber' : 'text-muted-foreground hover:text-foreground'}`} title={selectedNote.is_pinned ? 'Unpin' : 'Pin'} data-testid="editor-pin-btn">
          <Pin className={`w-4 h-4 ${selectedNote.is_pinned ? 'fill-current' : ''}`} />
        </button>
        <button onClick={() => toggleFavorite(selectedNoteId)} className={`p-2 rounded-sm transition-colors ${selectedNote.is_favorited ? 'text-yellow-500' : 'text-muted-foreground hover:text-foreground'}`} title={selectedNote.is_favorited ? 'Unfavorite' : 'Favorite'} data-testid="editor-favorite-btn">
          <Star className={`w-4 h-4 ${selectedNote.is_favorited ? 'fill-current' : ''}`} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-sm text-muted-foreground hover:text-foreground" data-testid="editor-more-menu">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {!isJournalEntry && (
              <DropdownMenuItem onClick={() => setShowPublish(true)} className="min-h-[40px]" data-testid="editor-publish-option"><Globe className="w-4 h-4 mr-2.5" /> Publish</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => exportNoteToPDF(selectedNote)} className="min-h-[40px]" data-testid="editor-pdf-export-option"><FileDown className="w-4 h-4 mr-2.5" /> Export as PDF</DropdownMenuItem>
            {!isJournalEntry && (
              <DropdownMenuItem onClick={() => setShowReminder(true)} className="min-h-[40px]" data-testid="editor-reminder-option"><Clock className="w-4 h-4 mr-2.5" /> Set Reminder</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setShowHistory(true)} className="min-h-[40px]" data-testid="editor-history-option"><Undo2 className="w-4 h-4 mr-2.5" /> Version History</DropdownMenuItem>
            <DropdownMenuSeparator />
            {!isJournalEntry && (
              <>
                <DropdownMenuItem className="min-h-[40px]" data-testid="editor-notebook-option"><FolderOpen className="w-4 h-4 mr-2.5" /> {noteNotebook ? noteNotebook.name : 'Set Notebook'}</DropdownMenuItem>
                <DropdownMenuItem className="min-h-[40px]" data-testid="editor-tag-option"><Tag className="w-4 h-4 mr-2.5" /> Manage Tags</DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {activeView === 'trash' ? (
              <>
                <DropdownMenuItem onClick={() => restoreNote(selectedNoteId)} className="min-h-[40px]" data-testid="editor-restore-option"><Undo2 className="w-4 h-4 mr-2.5" /> Restore</DropdownMenuItem>
                <DropdownMenuItem onClick={() => permanentDeleteNote(selectedNoteId)} className="min-h-[40px] text-red-400" data-testid="editor-perm-delete-option"><Trash className="w-4 h-4 mr-2.5" /> Delete Forever</DropdownMenuItem>
              </>
            ) : activeView === 'archived' ? (
              <DropdownMenuItem onClick={() => restoreNote(selectedNoteId)} className="min-h-[40px]" data-testid="editor-restore-option"><Undo2 className="w-4 h-4 mr-2.5" /> Restore</DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem onClick={() => archiveNote(selectedNoteId)} className="min-h-[40px]" data-testid="editor-archive-option"><Archive className="w-4 h-4 mr-2.5" /> Archive</DropdownMenuItem>
                <DropdownMenuItem onClick={() => deleteNote(selectedNoteId)} className="min-h-[40px] text-red-400" data-testid="editor-delete-option"><Trash className="w-4 h-4 mr-2.5" /> Move to Trash</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
